import { Clinic } from "@/data/Clinics";
import {
  deleteRecord,
  getRecord,
  getRecords,
  insertRecord,
  updateRecord,
} from "@/utils/helpers/data";
import { TABLES } from "@/utils/constants";
import { supabase } from "@/utils/supabase";
import { Access, WeeklyReportSubscriptionType } from "@/utils/types";
import { formatISO } from "date-fns";
import { now } from "@/utils/helpers/now";

export const COLUMNS = {
  ID: "id",
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  CLINIC_ID: "clinic_id",
  CREATED_AT: "created_at",
  UPDATED_AT: "updated_at",
  MOBILE_NUMBER: "mobile_number",
  USER_ID: "user_id",
  ACCESS: "access",
  PATIENT_VERIFICATION_ENABLED: "patient_verification_enabled",
  SPECIALIZATION: "specialization",
  SHOW_PROFILE_TIPS: "show_profile_tips",
  SHOW_CALENDAR_TIPS: "show_calendar_tips",
  BADGE: "badge",
  WEEKLY_REPORT_SUBSCRIPTION: "weekly_report_subscription",
  DELETED_AT: "deleted_at",
};

export interface Dentist {
  id?: string;
  first_name: string;
  last_name: string;
  clinic_id: string;
  created_at?: string;
  updated_at?: string;
  mobile_number?: string | null;
  user_id?: string | null;
  clinic?: Clinic;
  access?: Access | null;
  patient_verification_enabled?: boolean;
  specialization?: string | null;
  show_profile_tips?: boolean;
  show_calendar_tips?: boolean;
  badge?: number | null;
  weekly_report_subscription?: WeeklyReportSubscriptionType | null;
  deleted_at?: string | null;
}

/**
 * Creates a new dentist record in the database.
 * @param dentist - The dentist data to insert.
 * @returns The created dentist or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createDentist = async (
  dentist: Dentist,
): Promise<Dentist | null> => {
  return insertRecord<Dentist>(TABLES.DENTISTS, dentist);
};

/**
 * Updates an existing dentist record in the database.
 * @param dentistId - The ID of the dentist to update.
 * @param dentist - The updated dentist data.
 * @returns The updated dentist or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateDentist = async ({
  dentistId,
  dentist,
}: {
  dentistId: string | null;
  dentist: Dentist;
}): Promise<Dentist | null> => {
  if (!dentistId) return null;

  const { badge, ...updatedDentist } = dentist;
  return updateRecord<Dentist>(
    TABLES.DENTISTS,
    COLUMNS.ID,
    dentistId,
    updatedDentist,
  );
};

/**
 * Retrieves a single dentist by their ID.
 * @param dentistId - The ID of the dentist to retrieve.
 * @returns The requested dentist or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentist = async (
  dentistId: string | null,
): Promise<Dentist | null> => {
  if (!dentistId) return null;
  return getRecord<Dentist>(TABLES.DENTISTS, COLUMNS.ID, dentistId);
};

/**
 * Retrieves all dentists associated with a specific clinic ID.
 * @param clinicId - The ID of the clinic.
 * @returns An array of dentists.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentistsByClinicId = async (
  clinicId: string | null,
): Promise<Dentist[]> => {
  if (!clinicId) return [];
  return getRecords<Dentist>(TABLES.DENTISTS, COLUMNS.CLINIC_ID, clinicId);
};

/**
 * Retrieves all dentists associated with a specific user ID.
 * @param userId - The ID of the user.
 * @returns An array of dentists.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentistsByUserId = async (
  userId: string | null,
): Promise<Dentist[]> => {
  if (!userId) return [];
  const { data, error } = await supabase
    .from(TABLES.DENTISTS)
    .select("*, clinic:clinic_id (*)")
    .eq(COLUMNS.USER_ID, userId)
    .is(COLUMNS.DELETED_AT, null)
    .order(COLUMNS.CREATED_AT, { ascending: false });

  if (error) {
    throw new Error(`Error fetching dentists by user ID: ${error.message}`);
  }

  return data ?? [];
};

/**
 * Retrieves all dentists by their mobile number.
 * @param mobileNumber - The mobile number of the dentists.
 * @returns An array of dentists.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentistsByMobileNumber = async (
  mobileNumber: string | null,
): Promise<Dentist[]> => {
  if (!mobileNumber) return [];

  return getRecords<Dentist>(
    TABLES.DENTISTS,
    COLUMNS.MOBILE_NUMBER,
    mobileNumber,
  );
};

/**
 * Updates the show_profile_tips field for a dentist.
 * @param dentistId - The ID of the dentist to update.
 * @param showProfileTips - The value to set for show_profile_tips.
 * @returns The updated dentist or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateDentistShowProfileTips = async (
  dentistId: string,
  showProfileTips: boolean,
): Promise<Dentist | null> => {
  return updateRecord<Dentist>(TABLES.DENTISTS, COLUMNS.ID, dentistId, {
    [COLUMNS.SHOW_PROFILE_TIPS]: showProfileTips,
  });
};

/**
 * Updates the show_calendar_tips field for a dentist.
 * @param dentistId - The ID of the dentist to update.
 * @param showCalendarTips - The value to set for show_calendar_tips.
 * @returns The updated dentist or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateDentistShowCalendarTips = async (
  dentistId: string,
  showCalendarTips: boolean,
): Promise<Dentist | null> => {
  return updateRecord<Dentist>(TABLES.DENTISTS, COLUMNS.ID, dentistId, {
    [COLUMNS.SHOW_CALENDAR_TIPS]: showCalendarTips,
  });
};

/**
 * Permanently deletes a dentist record from the database.
 * @param dentistId - The ID of the dentist to delete.
 * @throws Will throw an error if the deletion fails.
 */
export const deleteDentist = async (
  dentistId: string | null,
): Promise<void> => {
  if (!dentistId) return;

  await deleteRecord(TABLES.DENTISTS, COLUMNS.ID, dentistId);
};

/**
 * Soft deletes a dentist record from the database.
 * @param dentistId - The ID of the dentist to delete.
 * @throws Will throw an error if the deletion fails.
 */
export const softDeleteDentist = async (
  dentistId: string | null,
): Promise<void> => {
  if (!dentistId) return;
  await updateRecord(TABLES.DENTISTS, COLUMNS.ID, dentistId, {
    [COLUMNS.DELETED_AT]: formatISO(now()),
  });
};
